import { Button, ButtonProps } from '@mui/material'
import { Device } from '../types'
import { useTranslation } from 'react-i18next'
import { usePolicy } from '../contexts/PolicyContext'
import useDialog from '../hooks/useDialog'
import DeviceDetachDialog from './DeviceDetachDialog'
import { LinkOff } from '@mui/icons-material'
import { useLocation, useNavigate } from 'react-router-dom'

export interface DeviceDetachButtonProps extends Omit<ButtonProps, 'onClick' | 'children'> {
  /**
   * The device
   */
  device: Device

  /**
   * The button label.
   */
  label?: string

  /**
   * The hook after the detach.
   */
  afterDetach?: (device: Device) => void
}

export default function DeviceDetachButton({
  device,
  afterDetach,
  label,
  ...props
}: DeviceDetachButtonProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The navigate function.
   */
  const navigate = useNavigate()

  /**
   * The location object.
   */
  const location = useLocation()

  /**
   * The dialog.
   */
  const { open, openDialog, closeDialog } = useDialog()

  /**
   * The policy state.
   */
  const { canEditDevice } = usePolicy()

  /**
   * Handle after the submit.
   */
  function handleAfterSubmit(device: Device) {
    if (afterDetach) {
      afterDetach(device)
    } else {
      const devicePath = `/devices/${device.name}`
      const isOnDevicePage = location.pathname === devicePath
      const isOnDeviceSubPage = location.pathname.startsWith(`${devicePath}/`)

      if (device?.asset?.projectId) {
        navigate(`/projects/${device.asset.projectId}#monitoring`)
      } else if (isOnDevicePage) {
        navigate(0)
      } else {
        navigate(devicePath, { replace: isOnDeviceSubPage })
      }
    }

    closeDialog()
  }

  return (
    <>
      {canEditDevice(device) && (
        <Button
          variant="text"
          color="warning"
          startIcon={<LinkOff />}
          {...props}
          onClick={openDialog}
        >
          {label ?? t('detachFromProject')}
        </Button>
      )}

      <DeviceDetachDialog
        device={device}
        open={open}
        onClose={closeDialog}
        afterSubmit={handleAfterSubmit}
      />
    </>
  )
}
